import { useCallback, useEffect, useRef } from 'react';

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export const isSpeechRecognitionSupported =
  typeof window !== 'undefined' &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

interface UseSpeechRecognitionOptions {
  lang?: string;
  onListeningStart?: () => void;
  onTranscript?: (text: string) => void;
  // Called when recognition ends or errors and we return to idle (without a result).
  onEnd?: () => void;
}

/**
 * Encapsulates the Web Speech API recognition lifecycle shared by the voice
 * input buttons. Callers keep their own UI state; this hook owns the
 * recognition object and translates its events into the three callbacks.
 */
export function useSpeechRecognition({
  lang = 'fr-CA',
  onListeningStart,
  onTranscript,
  onEnd,
}: UseSpeechRecognitionOptions) {
  const recognitionRef = useRef<any>(null);
  const listeningRef = useRef(false);

  // Stop any in-flight recognition when the consumer unmounts.
  useEffect(() => () => recognitionRef.current?.stop(), []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const recognition = new SR();
    recognitionRef.current = recognition;

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      listeningRef.current = true;
      onListeningStart?.();
    };

    recognition.onresult = (event: any) => {
      const result = event.results[event.results.length - 1];
      if (result.isFinal) {
        listeningRef.current = false;
        onTranscript?.(result[0].transcript);
      }
    };

    recognition.onerror = () => {
      listeningRef.current = false;
      onEnd?.();
    };

    recognition.onend = () => {
      if (listeningRef.current) {
        listeningRef.current = false;
        onEnd?.();
      }
    };

    recognition.start();
  }, [lang, onListeningStart, onTranscript, onEnd]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    listeningRef.current = false;
  }, []);

  return { start, stop, supported: isSpeechRecognitionSupported };
}
